// ./src/components/features/DisplayZoneAssignments/DisplayZoneAssignments.tsx
import React, { useMemo, useCallback } from "react";
import {
  Stack,
  Group,
  Text,
  Paper,
  Box,
  Alert,
  useMantineTheme,
} from "@mantine/core";
import { AppIcon } from "@/components/generic";
import { DisplayLayout } from "@/utils/api";
import {
  DisplayZoneCard,
  DisplayZoneCardValue,
  ZoneLayoutInfo,
} from "../DisplayZoneCard";

export interface DisplayZoneAssignmentsProps {
  /** Selected display layout with layoutConfig */
  layout: DisplayLayout | null | undefined;

  /** Current zone assignments */
  value: DisplayZoneCardValue[] | null | undefined;

  /** Callback when zone assignments change */
  onChange: (value: DisplayZoneCardValue[]) => void;

  /** Venue ID for context */
  venueId?: string | null;

  /** Validation errors map by zone ID */
  errors?: Record<string, any>;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

interface LayoutConfig {
  version?: string;
  layoutType?: string;
  main?: {
    gap?: string;
    zones?: Array<{
      id: string;
      widthPercent: number;
    }>;
  };
}

export const DisplayZoneAssignments: React.FC<DisplayZoneAssignmentsProps> = ({
  layout,
  value,
  onChange,
  venueId,
  errors,
  disabled = false,
  size = "md",
}) => {
  const theme = useMantineTheme();

  // Parse zones from layout config
  const zones: ZoneLayoutInfo[] = useMemo(() => {
    if (!layout?.layoutConfig) return [];

    try {
      const config: LayoutConfig =
        typeof layout.layoutConfig === "string"
          ? JSON.parse(layout.layoutConfig)
          : layout.layoutConfig;

      return config.main?.zones || [];
    } catch (error) {
      console.error("Failed to parse layout config:", error);
      return [];
    }
  }, [layout]);

  // Create a map of current zone values by zoneId
  const zoneValuesMap = useMemo(() => {
    const map = new Map<string, DisplayZoneCardValue>();
    if (value) {
      value.forEach((zoneValue) => {
        map.set(zoneValue.displayZoneId, zoneValue);
      });
    }
    return map;
  }, [value]);

  // Handle individual zone change
  const handleZoneChange = useCallback(
    (zoneValue: DisplayZoneCardValue) => {
      const newValues = [...(value || [])];
      const existingIndex = newValues.findIndex(
        (v) => v.displayZoneId === zoneValue.displayZoneId,
      );

      if (existingIndex >= 0) {
        newValues[existingIndex] = zoneValue;
      } else {
        newValues.push(zoneValue);
      }

      onChange(newValues);
    },
    [value, onChange],
  );

  // Check if no layout is selected
  if (!layout) {
    return (
      <Alert color="blue" icon={<AppIcon iconName="InfoCircle" size={16} />}>
        <Text size="sm">
          Please select a display layout first to configure zones.
        </Text>
      </Alert>
    );
  }

  // Check if layout has no zones
  if (zones.length === 0) {
    return (
      <Alert
        color="yellow"
        icon={<AppIcon iconName="AlertTriangle" size={16} />}
      >
        <Text size="sm">
          The selected layout does not have any configurable zones.
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Layout Preview */}
      <Paper withBorder p="md">
        <Stack gap="sm">
          <Group gap="xs">
            <AppIcon iconName="Layout" size={18} />
            <Text size="sm" fw={600}>
              Zone Layout Preview
            </Text>
          </Group>

          <Group gap={4} wrap="nowrap" align="stretch">
            {zones.map((zone) => {
              const zoneValue = zoneValuesMap.get(zone.id);
              const isConfigured = zoneValue?.displayZoneType !== null;
              const zoneColor = !isConfigured
                ? "gray"
                : zoneValue?.displayZoneType === "content_blocks"
                  ? "blue"
                  : "green";

              return (
                <Box
                  key={zone.id}
                  style={{
                    flex: `0 0 ${zone.widthPercent}%`,
                    minHeight: "60px",
                    backgroundColor: theme.colors[zoneColor][1],
                    border: `2px solid ${theme.colors[zoneColor][4]}`,
                    borderRadius: theme.radius.sm,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: theme.spacing.xs,
                  }}
                >
                  <Stack gap={4} align="center">
                    <Text size="xs" fw={600} c={zoneColor}>
                      {zone.id}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {zone.widthPercent}%
                    </Text>
                    {isConfigured && (
                      <Text size="xs" c={zoneColor}>
                        {zoneValue?.displayZoneType === "content_blocks"
                          ? "Content"
                          : "Schedule"}
                      </Text>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Group>

          <Text size="xs" c="dimmed">
            Configure each zone below. Blue = Content, Green = Schedule, Gray =
            Unconfigured
          </Text>
        </Stack>
      </Paper>

      {/* Zone Configuration Cards */}
      <Stack gap="sm">
        {zones.map((zone) => {
          const zoneValue = zoneValuesMap.get(zone.id);
          const zoneErrors = errors?.[zone.id];

          return (
            <DisplayZoneCard
              key={zone.id}
              zoneLayout={zone}
              value={zoneValue}
              onChange={handleZoneChange}
              venueId={venueId}
              errors={zoneErrors}
              disabled={disabled}
              size={size}
            />
          );
        })}
      </Stack>

      {/* Configuration Summary */}
      <Paper
        withBorder
        p="sm"
        style={{ backgroundColor: theme.colors.gray[0] }}
      >
        <Group gap="xs">
          <AppIcon iconName="InfoCircle" size={14} />
          <Text size="xs" c="dimmed">
            {zoneValuesMap.size} of {zones.length} zones configured
          </Text>
        </Group>
      </Paper>
    </Stack>
  );
};
